import { useCallback, useEffect, useMemo } from 'react';

import { Button } from '@bambi/component-library/src/components/Button/Button';
import { CoreFormFieldSelect } from '@bambi/component-library/src/components/forms/fields/CoreFormFieldSelect';
import { CheckBox } from '@bambi/component-library/src/components/inputs/CheckBox/CheckBox';
import { groupBy, sortBy, uniq } from 'lodash-es';
import { DateTime } from 'luxon';
import { useFormContext } from 'react-hook-form';

import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import { PassengerRead } from 'src/common/external/bambi-api/bambiApi';
import { RouteMultiloadTemplateRead } from 'src/common/external/bambi-api/routesApi';
import { ModalFooter } from 'src/common/primitives/Modal/ModalFooter';
import { show } from 'src/common/primitives/Toast/toast.slice';
import { formatFullName } from 'src/common/util/formatFullName';
import { orderMultiloadItems } from 'src/features/dispatch/multi-loading/useMultiloadOrderItems';
import { TripCardInternal } from 'src/features/dispatch/trips/TripCard/TripCard';

import { TripSpaceTypeBadge } from '../../management/TripSpaceTypeBadge';
import { routeTemplateActions } from '../routeTemplates.slice';
import { convertLocalDaysToDaysOfWeek } from './daysOfWeekMaps';
import { RouteTemplateFormValues, RouteTripTemplateFormValues } from './types';

interface RouteTemplateFormStep0Props {
  onNext: () => void;
  onCancel: () => void;
}
export function RouteTemplateFormStep0({
  onNext,
  onCancel,
}: RouteTemplateFormStep0Props) {
  const dispatch = useAppDispatch();
  const form = useFormContext();

  const assignments = useAppSelector(
    (state) => state.routeTemplates.bulkCreationAssignments
  );

  const assignmentOptions = useMemo(() => {
    return assignments.map((assignment) => {
      const driverName = formatFullName(assignment.driver);
      return {
        label: `${driverName}: ${assignment.vehicle.nickname}`,
        value: assignment.id,
      };
    });
  }, [assignments]);

  const trips = useAppSelector(
    (state) => state.routeTemplates.bulkCreationTrips
  );

  const selectedTripsMap = useAppSelector(
    (state) => state.routeTemplates.selectedTrips
  );

  const selectedTripsList = trips.filter((trip) => {
    return selectedTripsMap[trip.id];
  });

  const selectedAssignment = useAppSelector(
    (state) => state.routeTemplates.selectedAssignment
  );

  const selectedAssignmentOption = useMemo(() => {
    return assignmentOptions.find(
      (assignment) => assignment.value === selectedAssignment?.id
    );
  }, [assignmentOptions, selectedAssignment?.id]);

  const filteredTrips = useMemo(() => {
    if (!selectedAssignment) {
      return [];
    }

    return trips.filter((trip) => trip.assignment_id === selectedAssignment.id);
  }, [selectedAssignment, trips]);

  // Preselect all trips associated with the selected assignment
  useEffect(() => {
    filteredTrips.forEach((trip) => {
      dispatch(routeTemplateActions.selectTrip(trip.id));
    });
  }, [dispatch, filteredTrips]);

  const anySelected = useAppSelector((state) => {
    return !!Object.values(state.routeTemplates.selectedTrips).find((n) => n);
  });

  const handleNext = useCallback(() => {
    const daysOfTheWeek = uniq(
      selectedTripsList.map((trip) => {
        return DateTime.fromISO(trip.scheduled_pickup_at).toFormat('cccc');
      })
    );

    try {
      const sortedTrips = sortBy(selectedTripsList, (trip) =>
        DateTime.fromISO(trip.scheduled_pickup_at).toMillis()
      );

      // Don't include non-multiloadable trips in the multiload trip templates
      const multiloadableTrips = sortedTrips.filter(
        (trip) => trip.multiload_id
      );

      // Group multiload trips together
      const multiloadedTrips = groupBy(multiloadableTrips, 'multiload_id');

      // Construct the multiload trip template
      const multiloadTripTemplates: RouteMultiloadTemplateRead[] =
        Object.entries(multiloadedTrips).flatMap(([multiloadId, trips]) => {
          // If the user only selects 1 part of a multiload, we don't need to
          // create a multiload trip template
          if (trips.length === 1) {
            return [];
          }

          const orderedTrips = orderMultiloadItems(trips);

          return {
            template_trip_location_details: orderedTrips.map((item) => ({
              order: item.order,
              trip_location_detail_id:
                item.type === 'pickup'
                  ? item.trip.pickup_trip_location_detail_id
                  : item.trip.dropoff_trip_location_detail_id,
            })),
          };
          // Casting beacuse the missing fields are not required during creation
        }) as RouteMultiloadTemplateRead[];

      const routeTemplate: Partial<RouteTemplateFormValues> = {
        name: '',
        days_of_week: convertLocalDaysToDaysOfWeek(daysOfTheWeek),
        default_driver_id: selectedAssignment?.driver.id!,
        default_driver_name: selectedAssignment?.driver
          ? formatFullName(selectedAssignment.driver)
          : '',
        start_time: DateTime.fromISO(
          sortedTrips[0].scheduled_pickup_at
        ).toFormat('HH:mm'),
        end_time: DateTime.fromISO(
          sortedTrips[sortedTrips.length - 1].scheduled_pickup_at
        ).toFormat('HH:mm'),
        range_start: DateTime.now().toSQLDate(),
        allow_rbr_trips: true,
        trip_templates: selectedTripsList.map((trip) => ({
          dropoff: {
            id: trip.dropoff_trip_location_detail_id,
            place_id: trip.dropoff_place_id,
            address_details: trip.dropoff_address,
          },
          pickup: {
            id: trip.pickup_trip_location_detail_id,
            place_id: trip.pickup_place_id,
            address_details: trip.pickup_address,
          },
          // Casting because overlapping details are correct
          passenger: trip.passenger as PassengerRead,
          passenger_id: trip.passenger.id,
          scheduled_pickup_time: DateTime.fromISO(
            trip.scheduled_pickup_at
          ).toFormat('HH:mm:ss'),
          space_type: trip.space_type,
          num_accompanying_passengers: trip.num_accompanying_passengers,
        })) as RouteTripTemplateFormValues[],
        multiload_trip_templates: multiloadTripTemplates,
      };

      dispatch(routeTemplateActions.setEditingRouteTemplate(routeTemplate));
      // Need to also reset the form because its been rendered
      form.reset(routeTemplate);
      onNext();
    } catch (e) {
      dispatch(
        show({
          title: 'Unable to generate Route Templates due to missing trip data',
        })
      );
    }
  }, [dispatch, form, onNext, selectedAssignment?.driver, selectedTripsList]);

  const canSelectDriver = assignments.length > 1;

  const anyChecked = useMemo(() => {
    return Object.values(selectedTripsMap).some((v) => v);
  }, [selectedTripsMap]);

  const toggleAll = useCallback(() => {
    filteredTrips.forEach((trip) => {
      if (anyChecked) {
        dispatch(routeTemplateActions.deselectTrip(trip.id));
      } else {
        dispatch(routeTemplateActions.selectTrip(trip.id));
      }
    });
  }, [dispatch, filteredTrips, anyChecked]);

  return (
    <div className="mt-4 flex flex-col gap-4">
      <p className="text-sm text-gray-500">
        {canSelectDriver
          ? 'Filter trips by driver assignment and then select the trip matcher to include in the route template.'
          : 'Select the trips to include in the route template.'}
      </p>
      {canSelectDriver ? (
        <CoreFormFieldSelect
          label="Select Driver Assignment"
          inputProps={{}}
          onChange={(option) => {
            const assignment = assignments.find(
              (assignment) => assignment.id === option.value
            );
            dispatch(routeTemplateActions.selectAssignment(assignment));
          }}
          options={assignmentOptions}
          value={selectedAssignmentOption}
        />
      ) : null}
      <div className="flex max-h-[40vh] flex-col gap-2 overflow-auto">
        <div className="flex flex-row items-center gap-2">
          <CheckBox
            checked={anyChecked}
            id="select-all"
            onCheckedChange={toggleAll}
          />{' '}
          Select all
        </div>
        {filteredTrips.map((trip) => (
          <div key={trip.id} className="flex flex-row items-center gap-2">
            <CheckBox
              checked={selectedTripsMap[trip.id]}
              id={`select-trip-${trip.id}`}
              onCheckedChange={(v) => {
                if (v) {
                  dispatch(routeTemplateActions.selectTrip(trip.id));
                } else {
                  dispatch(routeTemplateActions.deselectTrip(trip.id));
                }
              }}
            />
            <TripCardInternal
              variant="thin"
              trip={trip}
              hideMenu
              adornment={
                <div>
                  <TripSpaceTypeBadge spaceType={trip.space_type} />
                </div>
              }
            />
          </div>
        ))}
      </div>
      <ModalFooter>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="primary" disabled={!anySelected} onClick={handleNext}>
          Next
        </Button>
      </ModalFooter>
    </div>
  );
}
